import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router";
import { Dialog } from "@progress/kendo-react-dialogs";
import { Switch } from "@progress/kendo-react-inputs";
import { icon, label, notifyIcon, number, quote } from "../../config/constants";
import { deleteWorkflowForm, editWorkflowForm, generateFormAiDescription, getWorkflowFormById } from "../../shared/services/workflow.services";
import { getNotification } from "../../utils/common";
import DescriptionPopup from "../Workflow/WorkflowBasicElements/DescriptionPopup";
import FormBackgroundColor from "./FormSettings/FormBackgroundColor";
import FormPrivacy from "./FormSettings/FormPrivacy";
import { FORM_TABS, FORM_TABS_KEYS } from "./FormsConstant";
import { useFormMetadata } from "./contexts/FormMetadataContext";
import "./styles/FormSettings.scss";

/**
 * Form Settings Sidebar Component
 * Contains form-level settings like enable/disable, privacy, delete, and duplicate
 */
const FormSettings = ({
  isOpen,
  onClose,
  formId,
  isNewForm,
  currentFormData,
  formData,
  onFormBgColorChange,
  isFormEnabled,
  setIsFormEnabled,
  isFormHidden,
  setIsFormHidden,
  privacyLevel,
  setPrivacyLevel,
  onFormDeleted,
  onPrivacyChange,
  privacyOptions,
  projectSettings,
  user,
  projectId,
  setFormTabs,
  onEditWorkflow,
}) => {
  const dispatch = useDispatch();
  const { workflowId } = useParams();
  const [isSwitchLoading, setIsSwitchLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [aiDescription, setAiDescription] = useState(currentFormData?.aiDescription || "");
  const [showDescPopup, setShowDescPopup] = useState(false);
  const [descPopupEnable, setDescPopupEnable] = useState(false);
  const [isGeneratingAi, setIsGeneratingAi] = useState(false);

  const [selectedTeams, setSelectedTeams] = useState([]);
  const [isUsingCustomPrivacy, setIsUsingCustomPrivacy] = useState(false);
  const userOpenedCustomModeRef = useRef(false);
  const hasInitializedTeamsRef = useRef(false);

  const allProjectsList = useSelector((state) => state.tasks.defaultDetails.allProjectsList);

  // Transform allProjectsList data for MultiSelect (same as DescriptionSetting.js)
  const teamData = useMemo(() => {
    if (!allProjectsList || !Array.isArray(allProjectsList)) return [];

    return allProjectsList
      .filter((project) => project.QueueId !== null)
      .map((project) => ({
        TeamId: project.ProjectId.toString(),
        TeamName: project.ProjectName,
        QueueName: project.QueueName,
        QueueId: project.QueueId,
      }));
  }, [allProjectsList]);

  /**
   * Initialize selected teams from allowedProjectIds when form loads
   * This ensures teams are ready before opening the MultiSelect
   * @author Bhavana
   */
  useEffect(() => {
    if (formId) {
      hasInitializedTeamsRef.current = false;
    }
  }, [formId]);

  useEffect(() => {
    if (isNewForm || hasInitializedTeamsRef.current) {
      return;
    }

    if (
      privacyLevel?.value === 4 &&
      currentFormData?.allowedProjectIds &&
      Array.isArray(currentFormData.allowedProjectIds) &&
      currentFormData.allowedProjectIds.length > 0 &&
      teamData.length > 0
    ) {
      const allowedIdsSet = new Set(currentFormData.allowedProjectIds.map((id) => id?.toString()).filter(Boolean));

      const preSelectedTeams = teamData.filter((team) => allowedIdsSet.has(team?.TeamId));

      if (preSelectedTeams.length > 0) {
        setSelectedTeams(preSelectedTeams);
        setIsUsingCustomPrivacy(true);
        hasInitializedTeamsRef.current = true;
      }
    }
  }, [isNewForm, privacyLevel?.value, currentFormData?.allowedProjectIds, teamData, formId]);

  useEffect(() => {
    const fetchAiDescription = async () => {
      if (formId && !isNewForm && isOpen) {
        try {
          const response = await getWorkflowFormById({ id: formId });
          if (response?.aiDescription !== undefined) {
            setAiDescription(response?.aiDescription || "");
          }
        } catch (error) {}
      } else if (currentFormData?.aiDescription !== undefined) {
        setAiDescription(currentFormData.aiDescription || "");
      }
    };

    fetchAiDescription();
  }, [formId, isNewForm, isOpen]);

  const handleFormEnabledChange = async (e) => {
    const newIsActive = e.target.value;

    // Don't make API call for new forms
    if (isNewForm || !formId) {
      setIsFormEnabled(newIsActive);
      return;
    }

    setIsSwitchLoading(true);
    try {
      // Prepare the payload for editWorkflowForm
      const updatePayload = {
        id: formId,
        workflowId: currentFormData?.workflowId,
        isActive: newIsActive,
      };

      const response = await dispatch(editWorkflowForm(updatePayload));

      if (response) {
        setIsFormEnabled(newIsActive);
        getNotification(quote.ON_UPDATE, notifyIcon.SUCCESS_ICON);
      }
    } catch (error) {
      setIsFormEnabled(!newIsActive);
    } finally {
      setIsSwitchLoading(false);
    }
  };

  const handleDuplicate = () => {
    // TODO: Implement duplicate functionality
  };

  const handleDelete = () => {
    if (isNewForm || !formId) {
      return;
    }
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await deleteWorkflowForm({ id: formId, workflowId });
      setShowDeleteModal(false);
      onFormDeleted?.(); // Notify parent component
    } catch (error) {
      // TODO: Show error notification
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
  };

  /**
   * Opens the AI description popup
   */
  const handleShowDescPopup = () => {
    setShowDescPopup(true);
    setDescPopupEnable(true);
  };

  /**
   * Closes the AI description popup
   */
  const closeDescriptionPopup = () => {
    setShowDescPopup(false);
    setDescPopupEnable(false);
  };

  /**
   * Updates the AI description when saved from the popup
   */
  const updateAiDescription = async () => {
    if (isNewForm || !formId) {
      return;
    }

    try {
      const updatePayload = {
        id: formId,
        workflowId: currentFormData?.workflowId,
        aiDescription: aiDescription,
      };
      const response = await dispatch(editWorkflowForm(updatePayload));
      if (response) {
        getNotification(quote.ON_UPDATE, notifyIcon.SUCCESS_ICON);
      }
    } catch (error) {
      getNotification(quote.ON_ERROR, notifyIcon.ERROR_ICON);
    }
  };

  /**
   * Handles generating AI description for the form
   */
  const handleGenerateAiDescription = async () => {
    if (isNewForm || !formId) {
      return;
    }

    setIsGeneratingAi(true);
    try {
      const response = await generateFormAiDescription({ id: formId });
      if (response?.aiDescription) {
        setAiDescription(response.aiDescription);
        getNotification(quote.ON_UPDATE, notifyIcon.SUCCESS_ICON);
      }
    } catch (error) {
      getNotification(quote.ON_ERROR, notifyIcon.ERROR_ICON);
    } finally {
      setIsGeneratingAi(false);
    }
  };

  const handleFormHiddenToggle = async (e) => {
    const newIsHidden = e.target.value;
    // Don't make API call for new forms
    if (isNewForm || !formId) {
      setIsFormHidden(newIsHidden);
      return;
    }
    setIsSwitchLoading(true);
    try {
      // Prepare the payload for editWorkflowForm
      const updatePayload = {
        id: formId,
        workflowId: currentFormData?.workflowId,
        isHiddenSection: newIsHidden,
      };
      const response = await dispatch(editWorkflowForm(updatePayload));
      if (response) {
        setIsFormHidden(newIsHidden);
        setFormTabs((prev) => ({
          ...prev,
          isHiddenSection: newIsHidden,
          ...FORM_TABS[FORM_TABS_KEYS[number.ZERO]],
        }));
        getNotification(quote.ON_UPDATE, notifyIcon.SUCCESS_ICON);
      }
    } catch (error) {
      setIsFormHidden(!newIsHidden);
    } finally {
      setIsSwitchLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className='form-settings-sidebar'>
        <div className='form-settings-header'>
          <h3 className='form-settings-title'>Form Settings</h3>
          <button className='form-settings-close' onClick={onClose} title='Close'>
            {icon.CLOSE}
          </button>
        </div>

        <div className='form-settings-content'>
          {/* Enabled/Disabled Switch */}
          <div className='form-setting-item '>
            <div className='d-flex justify-content-between'>
              <div className='setting-label'>{isFormEnabled ? "Enabled" : "Disabled"}</div>
              <div className='setting-control'>
                <Switch checked={isFormEnabled} onChange={handleFormEnabledChange} disabled={isSwitchLoading} />
              </div>
            </div>
            <div className='d-flex justify-content-between'>
              <div className='setting-description mt-2'>{isFormEnabled ? "Form is accepting inputs" : "Form is not accepting inputs"}</div>
            </div>
          </div>
          {/* Hidden/Visible Switch */}
          <div className='form-setting-item '>
            <div className='d-flex justify-content-between'>
              <div className='setting-label'>{label.REVIEW_SECTION}</div>
              <div className='setting-control'>
                <Switch checked={isFormHidden} onChange={handleFormHiddenToggle} disabled={isSwitchLoading || isNewForm} />
              </div>
            </div>
            <div className='d-flex justify-content-between'>
              <div className='setting-description mt-2'>{isFormHidden ? label.REVIEW_SECTION_VISIBLE : label.REVIEW_SECTION_HIDDEN}</div>
            </div>
          </div>
          <FormBackgroundColor formData={formData} onFormBgColorChange={onFormBgColorChange} />
          <FormPrivacy
            privacyOptions={privacyOptions}
            privacyLevel={privacyLevel}
            isNewForm={isNewForm}
            userOpenedCustomModeRef={userOpenedCustomModeRef}
            setSelectedTeams={setSelectedTeams}
            isUsingCustomPrivacy={isUsingCustomPrivacy}
            setPrivacyLevel={setPrivacyLevel}
            formId={formId}
            currentFormData={currentFormData}
            selectedTeams={selectedTeams}
            onPrivacyChange={onPrivacyChange}
            teamData={teamData}
            setIsUsingCustomPrivacy={setIsUsingCustomPrivacy}
            projectId={projectId}
            workflowId={workflowId}
            onEditWorkflow={onEditWorkflow}
          />

          {!isNewForm && (
            <div className='form-setting-item'>
              <div className='ai-description-header'>
                <h4 className='ai-description-title'>
                  AI Description <span className='ai-description-subtitle'>(for chatbot training)</span>
                </h4>
                <div
                  id='form-settings-ai-description-icon'
                  className='ai-description-icon cursor-pointer'
                  onClick={handleShowDescPopup}
                  title='Edit AI Description'>
                  {icon.DESCRIPTION}
                </div>
              </div>
              <div className='setting-description '>Updates may take a few hours to appear in the chatbot</div>
            </div>
          )}

          {/* Action Buttons */}
          <div className='form-setting-item'>
            <div className='setting-actions'>
              <button disabled={true} className='setting-action-btn disabled-action-btn' onClick={handleDuplicate}>
                <span className='action-icon'>{icon.COPY_CONTENT}</span>
                <div className='action-text-wrapper'>
                  <span className='action-text'>Duplicate Form</span>
                  <span className='coming-soon-label'>Coming Soon</span>
                </div>
              </button>

              <button className='setting-action-btn delete-btn' onClick={handleDelete}>
                <span className='action-icon'>{icon.DELETE_ICON}</span>
                <span className='action-text'>Delete</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <Dialog title='Delete Form' onClose={handleCancelDelete} className='delete-confirmation-modal'>
          <div className='delete-modal-content'>
            <div className='delete-modal-message'>Are you sure you want to delete this form? This action cannot be undone.</div>
            <div className='delete-modal-actions'>
              <button className='btn btn-ghost' onClick={handleCancelDelete}>
                Cancel
              </button>
              <button className='btn btn-primary' onClick={handleConfirmDelete}>
                Delete
              </button>
            </div>
          </div>
        </Dialog>
      )}

      {/* AI Description Popup */}
      {descPopupEnable && (
        <DescriptionPopup
          desc={aiDescription}
          setDesc={setAiDescription}
          showDescPopup={showDescPopup}
          setShowDescPopup={setShowDescPopup}
          updateDescription={updateAiDescription}
          closeDescriptionPopup={closeDescriptionPopup}
          headerLabel='Description'
          anchorElementId='form-settings-ai-description-icon'
          formId={formId}
          isFormSettings={true}
          formProjectId={projectId}
          onGenerateAi={handleGenerateAiDescription}
          isGeneratingAi={isGeneratingAi}
        />
      )}
    </>
  );
};

export default FormSettings;
